<template>
  <Modal
    class="scoped"
    :hide-close-button="step === 3"
    :modal-style="{ maxWidth: modalMaxWidth }"
    :persistent="step === 3"
    title="Import Organisation Teams"
  >
    <div style="max-width: 100vw">
      <QCardSection class="row items-center">
        <h4>Import Organisation Teams via CSV</h4>
      </QCardSection>
      <QCardSection v-if="step === 1">
        <div class="row">
          <QInput
            accept=".csv"
            class="full-width"
            filled
            hint="Upload csv"
            square
            type="file"
            @update:model-value="processCsvFile"
          />
        </div>
      </QCardSection>

      <QCardSection
        v-if="step === 1"
        align="right"
      >
        <VueJsonCsv
          class="col"
          :data="sampleCsvData"
          name="sample-organisation-teams.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample Organisation Teams.csv"
            no-caps
          />
        </VueJsonCsv>
      </QCardSection>

      <QCardSection v-if="step === 2">
        <div class="row">
          <QMarkupTable
            class="full-width import-table"
            style="height: 600px"
          >
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-left">Default Role</th>
                <th class="text-left">Brand Profile ID</th>
                <th class="text-left">Default Brief Set</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(organisationTeam, index) in teamsToImport"
                :key="index"
              >
                <td class="text-left">
                  <QInput
                    v-model="organisationTeam.name"
                    dense
                    filled
                    label="Name"
                    square
                  />
                </td>
                <td class="text-left">
                  <RoleDropdown
                    v-model="organisationTeam.role_id"
                    hide-view-button
                  />
                </td>
                <td class="text-left">
                  <SelectList
                    v-model="organisationTeam.brand_profile_id"
                    class="q-mt-md"
                    clearable
                    dense
                    emit-value
                    label="Select Brand Profile"
                    option-label="name"
                    option-value="id"
                    :options="organisationBrands"
                  />
                </td>
                <td>
                  <SelectList
                    v-model="organisationTeam.brief_set_id"
                    class="q-mt-md"
                    clearable
                    dense
                    emit-value
                    label="Select Brief Set"
                    option-label="name"
                    option-value="id"
                    :options="briefSets"
                  />
                </td>
              </tr>
            </tbody>
          </QMarkupTable>
        </div>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-between">
          <div>
            <QBtn
              v-if="step === 2"
              color="primary"
              flat
              label="Back"
              no-caps
              @click="step = 1"
            />
          </div>
          <div>
            <QBtn
              v-if="step === 1"
              color="black"
              :disabled="importDisabled"
              :label="reviewLabel"
              no-caps
              @click="step = 2"
            />
            <QBtn
              v-if="step === 2"
              v-close-popup
              color="black"
              :disabled="importDisabled"
              :label="importLabel"
              no-caps
              @click="confirm"
            />
          </div>
        </div>
      </QCardActions>
    </div>
  </Modal>
</template>

<script>
import Papa from "papaparse";
import { storeToRefs } from "pinia";
import VueJsonCsv from "vue-json-csv";

import Modal from "shared/components/core/modals/Modal.vue";
import { pluralize } from "shared/helpers/string";
import { BriefSet, OrganisationBrand } from "shared/resources";

import RoleDropdown from "src/components/common/RoleDropdown.vue";
import SelectList from "src/components/common/SelectList.vue";
import { useGlobalAdminStore } from "src/stores/global";

export default {
  name: "ImportOrganisationTeamsModal",
  components: {
    Modal,
    VueJsonCsv,
    RoleDropdown,
    SelectList,
  },
  props: {
    organisationId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ["confirm", "cancel"],
  setup() {
    const globalAdminStore = useGlobalAdminStore();
    const { accountManagers } = storeToRefs(globalAdminStore);

    return { accountManagers };
  },
  data() {
    return {
      step: 1,
      teamsToImport: [],
      importStatuses: [],
      organisationBrands: [],
      briefSets: [],
    };
  },
  computed: {
    sampleCsvData() {
      return [
        {
          name: "String - Required",
          role_id: "Number - Required",
          filter_set_id: "Number - Optional",
          brand_profile_id: "Number - Optional",
        },
        {
          name: "Sample Team",
          role_id: 1,
          filter_set_id: 1,
          brand_profile_id: 1,
        },
      ];
    },
    importDisabled() {
      return !this.teamsToImport.length;
    },
    pluralString() {
      return `${this.teamsToImport.length} ${pluralize(
        this.teamsToImport.length,
        "Team"
      )}`;
    },
    reviewLabel() {
      return `Review ${this.pluralString}`;
    },
    importLabel() {
      return `Import ${this.pluralString}`;
    },
    modalMaxWidth() {
      return this.step === 2 ? "100%" : "500px";
    },
    confirmMessage() {
      const failedOrganisations = this.importStatuses.filter(
        (status) => status.error
      );

      const failedRows = failedOrganisations
        .map(({ name, error }) => `<tr><td>${name}</td><td>${error}</td></tr>`)
        .join("\n");

      const successfulOrganisations =
        this.importStatuses.length - failedOrganisations.length;

      let message = `
        <strong>
          Sucessfully imported ${successfulOrganisations} of ${this.pluralString}
        </strong>
      `;

      if (failedOrganisations.length) {
        message += `
          <br/>
          <br/><strong>Failed Organisations:</strong><br/>
          <table class="q-table">
            <thead>
              <tr>
                <td>Name</td>
                <td>Fail Reason</td>
              </tr>
            </thead>
            <tbody>
              ${failedRows}
            </tbody>
          </table>
        `;
      }

      return message;
    },
  },
  mounted() {
    this.loadOrganisationBrands();
    this.loadBriefSets();
  },
  methods: {
    async loadBriefSets() {
      this.loading = true;

      try {
        const order = { name: "asc" };

        const response = await BriefSet.where({
          organisationId: { eq: this.organisationId },
        })
          .per(999)
          .order(order)
          .all();

        this.briefSets = response.data;
      } catch (error) {
        this.$error("Error loading Brief Set", error);
      } finally {
        this.loading = false;
      }
    },
    async loadOrganisationBrands() {
      try {
        const order = { name: "asc" };

        const response = await OrganisationBrand.where({
          organisationId: { eq: this.organisationId },
          archived: false,
        })
          .order(order)
          .per(999)
          .all();

        this.organisationBrands = response.data;
      } catch (error) {
        this.$error("There was an error loading Organisation Brands.", error);
      }
    },
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.teamsToImport = parsed.map((values) => {
          const teamAttributes = values.reduce((acc, value, index) => {
            if (columns[index] === "role_id") {
              acc[columns[index]] = Number(value);
            } else {
              acc[columns[index]] = value;
            }

            return acc;
          }, {});

          return teamAttributes;
        });
      };

      reader.readAsText(file);
    },
    async confirm() {
      try {
        const response = await this.$streemApiAdmin.post(
          `organisation_teams/import`,
          {
            params: {
              organisation_teams: this.teamsToImport,
              organisation_id: this.organisationId,
            },
          }
        );

        this.importStatuses = response.data;

        this.$dialog
          .confirm({
            html: true,
            title: "Import Complete",
            message: this.confirmMessage,
            persistent: true,
            ok: "Done",
          })
          .onOk(() => {
            this.cancel();
            this.$emit("confirm");
          });
      } catch (error) {
        this.$error(
          "There was an error importing the organisation teams",
          error
        );
      } finally {
        this.$q.loading.hide();
      }
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .import-table {
    .q-field {
      width: 200px;
    }

    .checkbox {
      width: 50px !important;
    }
  }
}
</style>
